import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { ToastNotifService } from '../../../../../core/services/toastNotif.service';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { LanguageService } from '../../../../../core/services/language.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatMomentDateModule,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import * as _moment from 'moment';
import { default as _rollupMoment, Moment } from 'moment';

const moment = _rollupMoment || _moment;

import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PssLoadingComponent } from "../../../../../tools/template/pss/pss-loading/pss-loading.component";
import { ToastCustomService } from '../../../../../tools/services/toast/toast-custom/toastCustom.service';
import { ParkingHistory } from '../../../../../core/models/parking.model';
import { ApiParkingService } from '../../../../../core/services/api/Parking/api-parking';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-parking-history-2',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatMomentDateModule,
    FormsModule,
    CommonModule,
    PssLoadingComponent,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './parking-history-2.component.html',
  styleUrl: './parking-history-2.component.css'
})
export class ParkingHistory2Component implements OnInit {
  statusOpenDetails: number = 0;
  currentLang: "TH" | "EN" = 'TH';
  sub!: Subscription;
  token: string = '';
  uid: string = '';
  startDate: string = ''; // ex: '2025-05-01'
  endDate: string = '';
  today: string = '';
  minStartDate: string = '';
  parking_history: ParkingHistory[] = [];
  isLoading = false;

  // Advanced UI Controls State
  showDashboard: boolean = true;
  showTableFilters: boolean = false;
  searchQuery: string = '';
  selectedSite: string = '';
  selectedVehicleType: string = '';
  searchFieldText: string = '';
  isSiteDropdownOpen: boolean = false;

  // Material Datepicker Moment States
  startDateMoment: _moment.Moment | null = null;
  endDateMoment: _moment.Moment | null = null;
  todayMoment: _moment.Moment = moment();
  minStartDateMoment: _moment.Moment = moment().subtract(3, 'months');

  constructor(
    private cookieService: CookieService,
    private apiParkingService: ApiParkingService,
    private toastCustomService: ToastCustomService,
    private router: Router,
    private languageService: LanguageService
  ) {
    this.sub = this.languageService.currentLang$.subscribe((lang) => {
      this.currentLang = lang;
    });
  }

  ngOnInit() {
    this.uid =
      this.cookieService.get('line_uid') ||
      localStorage.getItem('line_uid') ||
      '';
    this.token = '';

    // Initialize Moment dates
    const now = moment();
    const sevenDaysAgo = moment().subtract(7, 'days');
    const threeMonthsAgo = moment().subtract(3, 'months');

    this.startDateMoment = sevenDaysAgo;
    this.endDateMoment = now;
    this.todayMoment = now;
    this.minStartDateMoment = threeMonthsAgo;

    this.today = now.format('YYYY-MM-DD');
    this.minStartDate = threeMonthsAgo.format('YYYY-MM-DD');
    this.startDate = sevenDaysAgo.format('YYYY-MM-DD');
    this.endDate = this.today;

    setTimeout(() => {
      this.searchParkingHistory();
    }, 100);
  }

  // ================= Getters =================

  /**
   * Returns the filtered list of parking history records based on search query, site, and vehicle type.
   */
  get filteredParkingHistory(): ParkingHistory[] {
    let list = this.parking_history || [];

    // License Plate Search
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      const q = this.searchQuery.toLowerCase().trim();
      list = list.filter(item =>
        (item.entryLicense && item.entryLicense.toLowerCase().includes(q)) ||
        (item.exitLicense && item.exitLicense.toLowerCase().includes(q))
      );
    }

    // Site Name Filter
    if (this.selectedSite && this.selectedSite.trim() !== '') {
      list = list.filter(item => item.siteName === this.selectedSite);
    }

    // Vehicle Type Filter
    if (this.selectedVehicleType && this.selectedVehicleType.trim() !== '') {
      list = list.filter(item => item.vehicleTypeName === this.selectedVehicleType);
    }

    return list;
  }

  /**
   * Returns the total number of sessions in the filtered parking history.
   */
  get totalSessions(): number {
    return this.filteredParkingHistory.length;
  }

  /**
   * Returns the total parking time formatted as hours.
   */
  get totalHours(): number {
    const totalMinutes = this.filteredParkingHistory.reduce((sum, item) => sum + (item.totalTime || 0), 0);
    return Math.floor(totalMinutes / 60);
  }

  /**
   * Returns the average parking duration in minutes.
   */
  get averageTime(): number {
    if (this.filteredParkingHistory.length === 0) return 0;
    const totalMinutes = this.filteredParkingHistory.reduce((sum, item) => sum + (item.totalTime || 0), 0);
    return Math.round(totalMinutes / this.filteredParkingHistory.length);
  }

  /**
   * Returns the count of unique sites visited in the filtered list.
   */
  get uniqueSitesCount(): number {
    const sites = this.filteredParkingHistory.map(item => item.siteId);
    return new Set(sites).size;
  }

  /**
   * Returns a list of unique site names from the fetched history.
   */
  get siteOptions(): string[] {
    const sites = this.parking_history?.map(item => item.siteName) || [];
    return Array.from(new Set(sites)).filter(Boolean);
  }

  /**
   * Returns filtered site options matching the autocomplete input text.
   */
  get filteredSiteOptions(): string[] {
    const q = (this.searchFieldText || '').toLowerCase().trim();
    if (!q) {
      return this.siteOptions;
    }
    return this.siteOptions.filter(site => site.toLowerCase().includes(q));
  }

  /**
   * Returns a list of unique vehicle type names from the fetched history.
   */
  get vehicleTypeOptions(): string[] {
    const types = this.parking_history?.map(item => item.vehicleTypeName) || [];
    return Array.from(new Set(types)).filter(Boolean);
  }

  /*#################################################### Function ################################################*/

  /**
   * Toggles the visibility of the statistics dashboard.
   */
  toggleDashboard() {
    this.showDashboard = !this.showDashboard;
  }

  /**
   * Toggles the visibility of the advanced table filter panel.
   */
  toggleFilters() {
    this.showTableFilters = !this.showTableFilters;
  }

  /**
   * Handles selection of a start date from the datepicker.
   */
  onStartDateChange(event: any) {
    if (event.value) {
      this.startDateMoment = event.value;
      this.startDate = event.value.format('YYYY-MM-DD');
    }
  }

  /**
   * Handles selection of an end date from the datepicker.
   */
  onEndDateChange(event: any) {
    if (event.value) {
      this.endDateMoment = event.value;
      this.endDate = event.value.format('YYYY-MM-DD');
    }
  }

  /**
   * Selects a site in the custom searchable combo box.
   */
  selectSite(site: string) {
    this.selectedSite = site;
    this.searchFieldText = site;
    this.isSiteDropdownOpen = false;
  }

  /**
   * Validates and closes autocomplete dropdowns, reverting to the last valid value if needed.
   */
  closeDropdowns() {
    setTimeout(() => {
      this.isSiteDropdownOpen = false;

      const options = this.siteOptions || [];
      const matched = options.find(
        site => site.toLowerCase() === (this.searchFieldText || '').toLowerCase().trim()
      );

      if (matched) {
        this.selectedSite = matched;
        this.searchFieldText = matched;
      } else if (!this.searchFieldText || this.searchFieldText.trim() === '') {
        this.selectedSite = '';
        this.searchFieldText = '';
      } else {
        this.searchFieldText = this.selectedSite || '';
      }
    }, 250);
  }

  /**
   * Selects or toggles the vehicle type filter.
   */
  selectVehicleType(type: string) {
    if (this.selectedVehicleType === type) {
      this.selectedVehicleType = '';
    } else {
      this.selectedVehicleType = type;
    }
  }

  /**
   * Displays a warning toast notification.
   */
  showWarning(text: string, detail: string = '') {
    this.toastCustomService.showWarning('', text);
  }

  /**
   * Displays an error toast notification.
   */
  showError(text: string, detail: string = '') {
    this.toastCustomService.showError('', text);
  }

  /**
   * Displays a success toast notification.
   */
  showSuccess(text: string, detail: string = '') {
    this.toastCustomService.showSuccess('', text);
  }

  /**
   * Toggles the detail view display of a specific parking history card.
   */
  openDetails(id: number) {
    if (this.statusOpenDetails === id) {
      this.statusOpenDetails = 0;
    } else {
      this.statusOpenDetails = id;
    }
  }

  /**
   * Calculates the hour component of a total time in minutes.
   */
  hour(totalTime: number): number {
    return Math.floor(totalTime / 60);
  }

  /**
   * Navigates back to the home page.
   */
  goback() {
    this.router.navigate(['/home']);
  }

  /*############################################# API #################################################*/

  /**
   * Fetches parking history data within the specified start and end date range.
   */
  searchParkingHistory() {
    if (!this.startDate || !this.endDate) {
      this.showWarning(
        this.currentLang === 'TH'
          ? 'กรุณาระบุวันที่ค้นหาก่อน'
          : 'Please specify the search date first.'
      );
    } else {
      this.isLoading = true;
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);
      this.apiParkingService
        .getParkingHistory(this.token, this.uid, start, end)
        .subscribe({
          next: (payment) => {
            console.log('Parking history response:', payment);
            this.parking_history = payment || [];
            this.isLoading = false;
          },
          error: (err) => {
            console.error('Error fetching parking history:', err);
            this.parking_history = [];
            this.isLoading = false;
          },
        });
    }
  }
}
